import React from 'react';
import moment from 'moment';
import { CUSTOM_RANGE, TIME_RANGE_PRESETS } from './timeRangePresets';
import Select from '../select/Select';
import BasicDatePicker from '../basic-datepicker/BasicDatePicker';
import { toCalendarDate, toDayString } from '../basic-datepicker/calendarDay';
import type { TimeRange } from '../span-timeline/timeAxis';

type RangePickerProps = {
    /** A preset's value, or `custom`. */
    preset: string;
    /** The span currently on screen, whichever way it was chosen. */
    period: TimeRange;
    onPresetChange: (preset: string) => void;
    onCustomChange: (period: TimeRange) => void;
    /**
     * Distinguishes this picker's controls from another's on the same page.
     *
     * Two pickers sharing one prefix give the document duplicate `id`s and labels that reach
     * whichever came first — the same fault a date range has, one level down.
     */
    idPrefix?: string;
};

const asDay = (at: Date) => moment.utc(at).format('YYYY-MM-DD');

/**
 * Which span of time is on screen.
 *
 * A list rather than a pair of draggable handles: the question is nearly always "the last N of
 * something", which is one choice from a list and several drags otherwise. `Custom` keeps the
 * one thing a list of "past N" cannot express — a span with no relation to now.
 *
 * The span is in UTC, and the labels say so. A reader picks a day on their own calendar and the
 * whole of that UTC day is included, which is what a range over stored instants has to mean if
 * two readers in different places are to be shown the same thing.
 */
const RangePicker = ({ preset, period, onPresetChange, onCustomChange, idPrefix = 'range' }: RangePickerProps) => {
    const changeEdge = (edge: 'from' | 'to') => (value: string) => {
        const at =
            edge === 'from'
                ? moment.utc(value, 'YYYY-MM-DD').startOf('day').toDate()
                : moment.utc(value, 'YYYY-MM-DD').endOf('day').toDate();

        const next = { ...period, [edge]: at };

        // A span the wrong way round contains nothing, and reads as an empty result rather
        // than as the mistake it is.
        onCustomChange(next.from.getTime() <= next.to.getTime() ? next : { from: next.to, to: next.from });
    };

    return (
        <div className='flex flex-wrap items-end gap-3'>
            <Select
                label='Showing'
                testId={`${idPrefix}-preset`}
                width={180}
                selectedValue={preset}
                onChange={onPresetChange}
                options={[...TIME_RANGE_PRESETS.map(({ value, label }) => ({ value, label })), { value: CUSTOM_RANGE, label: 'Custom…' }]}
            />

            {preset === CUSTOM_RANGE && (
                <>
                    {/* The days are UTC. They cross into the picker as local midnight so a
                        reader west of Greenwich is not shown the day before — see `calendarDay`. */}
                    <div className='w-40'>
                        <BasicDatePicker
                            id={`${idPrefix}-custom-from`}
                            testId={`${idPrefix}-custom-from`}
                            label='From (UTC)'
                            value={toCalendarDate(asDay(period.from))}
                            onChange={chosen => changeEdge('from')(toDayString(chosen))}
                        />
                    </div>
                    <div className='w-40'>
                        <BasicDatePicker
                            id={`${idPrefix}-custom-to`}
                            testId={`${idPrefix}-custom-to`}
                            label='To (UTC)'
                            value={toCalendarDate(asDay(period.to))}
                            onChange={chosen => changeEdge('to')(toDayString(chosen))}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default RangePicker;
